import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { useFrameTurnaround } from '../hooks/useReports';
import { exportToCSV } from '@/shared/utils/exportUtils';
import { formatDate } from '@/shared/utils/dateUtils';
import type { FrameTurnaroundFilters } from '../services/reportsService';

interface FrameTurnaroundSectionProps {
  filters: FrameTurnaroundFilters;
}

export function FrameTurnaroundSection({ filters }: FrameTurnaroundSectionProps) {
  const { data, isLoading, error } = useFrameTurnaround(filters);

  const handleExport = () => {
    if (!data) return;

    exportToCSV(
      data.map((item) => ({
        Manufacturer: item.manufacturer_name,
        Location: item.location_name,
        'Order Type': item.order_type,
        'Avg Days: Entry to Ordered': item.avg_days_entry_to_ordered || '',
        'Avg Days: Ordered to Received': item.avg_days_ordered_to_received || '',
        'Avg Total Days': item.avg_total_days || '',
        'Sample Size': item.sample_size,
      })),
      'frame-turnaround-report'
    );
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Frame Turnaround</h2>
        <div className="text-center py-8 text-gray-500">Loading...</div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Frame Turnaround</h2>
        <div className="text-center py-8 text-red-600">
          Error loading data: {error.message}
        </div>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Frame Turnaround</h2>
        <div className="text-center py-8 text-gray-500">
          No completed orders found in the selected date range
        </div>
      </Card>
    );
  }

  const getDaysColor = (days: number | null, threshold1: number, threshold2: number) => {
    if (days === null) return 'text-gray-400';
    if (days <= threshold1) return 'text-green-600';
    if (days <= threshold2) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold">Frame Turnaround</h2>
          <p className="text-sm text-gray-500 mt-1">
            Showing data from {formatDate(filters.date_from)} to{' '}
            {formatDate(filters.date_to)}
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={handleExport}>
          Export CSV
        </Button>
      </div>

      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded">
        <p className="text-sm text-blue-900">
          <strong>Note:</strong> This report only includes frames that have been fully
          received (complete cycle). It shows average time through each stage of the
          workflow to help identify bottlenecks.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Manufacturer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Order Type
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Entry → Ordered
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ordered → Received
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Cycle
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sample Size
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((item, index) => (
              <tr
                key={`${item.manufacturer_id}-${item.location_id}-${item.order_type}-${index}`}
                className="hover:bg-gray-50"
              >
                <td className="px-6 py-4 whitespace-nowrap font-medium">
                  {item.manufacturer_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                  <span className="hidden md:inline">{item.location_name}</span>
                  <span className="md:hidden">
                    {item.location_short_code || item.location_name}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Badge
                    variant={item.order_type === 'PATIENT' ? 'warning' : 'neutral'}
                  >
                    {item.order_type}
                  </Badge>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <span
                    className={`font-medium ${getDaysColor(
                      item.avg_days_entry_to_ordered,
                      2,
                      5
                    )}`}
                  >
                    {item.avg_days_entry_to_ordered !== null
                      ? `${item.avg_days_entry_to_ordered.toFixed(1)} days`
                      : '-'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <span
                    className={`font-medium ${getDaysColor(
                      item.avg_days_ordered_to_received,
                      7,
                      14
                    )}`}
                  >
                    {item.avg_days_ordered_to_received !== null
                      ? `${item.avg_days_ordered_to_received.toFixed(1)} days`
                      : '-'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <span
                    className={`font-medium ${getDaysColor(
                      item.avg_total_days,
                      10,
                      20
                    )}`}
                  >
                    {item.avg_total_days !== null
                      ? `${item.avg_total_days.toFixed(1)} days`
                      : '-'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500">
                  {item.sample_size}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-sm text-gray-500">
        Showing {data.length} manufacturer-location-order type combinations
      </div>
    </Card>
  );
}


